/**
 * Navigation routing service — Valhalla-backed via Coordal API.
 *
 * All turn-by-turn routing goes through POST /v1/navigation/directions.
 * Mapbox is used only for map rendering (see useMapInitialization).
 */

import { API_URL } from '../lib/env';
import { requireAuthToken } from './api';

import type { FloodExposureSummary } from '../lib/floodExposure';

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface RouteStep {
  instruction: string;
  distance: number;
  duration: number;
  mode?: string;
  name?: string;
  stop_type?: string;
  maneuver: {
    type: string;
    modifier?: string;
    bearing_before?: number;
    bearing_after?: number;
    location: [number, number];
    instruction?: string;
  };
}

export interface Route {
  distance: number;
  duration: number;
  duration_typical?: number | null;
  geometry: GeoJSON.LineString;
  steps: RouteStep[];
  summary: string;
  flood_exposure?: FloodExposureSummary | null;
}

export interface DirectionsResponse {
  routes: Route[];
  waypoints: {
    name: string;
    location: [number, number];
  }[];
}

export type TravelMode = 'driving' | 'walking' | 'cycling' | 'driving-traffic' | 'transit';

export interface DirectionsOptions {
  mode?: TravelMode;
  alternatives?: boolean;
  steps?: boolean;
  avoidTolls?: boolean;
  avoidHighways?: boolean;
  avoidFerries?: boolean;
  signal?: AbortSignal;
}

/** Transform Coordal navigation envelope → flat routes for the UI. */
export function transformDirectionsEnvelope(envelope: {
  data?: { routes?: Array<Record<string, unknown>> };
}): DirectionsResponse {
  const apiRoutes = envelope?.data?.routes ?? [];

  if (!apiRoutes.length) {
    throw new Error('No route found between the given coordinates');
  }

  return {
    routes: apiRoutes.map((r) => {
      const leg = (r.legs as Array<Record<string, unknown>> | undefined)?.[0] ?? {};
      const steps = (leg.steps as Array<Record<string, unknown>> | undefined) ?? [];
      return {
        distance: r.distance as number,
        duration: r.duration as number,
        duration_typical: (r.duration_typical as number | null | undefined) ?? null,
        geometry: r.geometry as GeoJSON.LineString,
        summary: (leg.summary as string) || '',
        flood_exposure: (r.flood_exposure as FloodExposureSummary | null | undefined) ?? null,
        steps: steps.map((s) => {
          const maneuver = (s.maneuver as Record<string, unknown> | undefined) ?? {};
          return {
            instruction: (maneuver.instruction as string) || (s.name as string) || '',
            distance: s.distance as number,
            duration: s.duration as number,
            maneuver: {
              type: (maneuver.type as string) || 'continue',
              modifier: maneuver.modifier as string | undefined,
              bearing_before: undefined,
              bearing_after: undefined,
              location: maneuver.location as [number, number],
            },
          };
        }),
      };
    }),
    waypoints: [],
  };
}

// ── Transit types ────────────────────────────────────────────────────────────

export interface TransitStop {
  id: number;
  name: string;
  type: string | null;
  sequence: number;
  role: 'origin' | 'intermediate' | 'destination';
  fraction: number;
  coordinates: { lat: number; lng: number };
}

export interface TransitProfile {
  stops: TransitStop[];
  stop_count: number;
  estimated_fare_ghs: number | null;
}

export interface TransitRouteResponse {
  route: Route;
  transit_profile: TransitProfile;
}

/** Get directions between two points via Valhalla-backed Coordal API. */
export async function getDirections(
  origin: Coordinates,
  destination: Coordinates,
  options: DirectionsOptions = {}
): Promise<DirectionsResponse> {
  const {
    mode = 'driving-traffic',
    alternatives = true,
    steps = true,
    signal,
  } = options;

  const token = requireAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  try {
    const response = await fetch(`${API_URL}/v1/navigation/directions`, {
      method: 'POST',
      headers,
      signal,
      body: JSON.stringify({
        origin: { latitude: origin.lat, longitude: origin.lng },
        destination: { latitude: destination.lat, longitude: destination.lng },
        profile: mode,
        alternatives,
        steps,
        banner_instructions: false,
        voice_instructions: false,
      }),
    });

    if (!response.ok) {
      let detail = 'Failed to get directions';
      try {
        detail = (await response.json()).detail || detail;
      } catch {
        /* ignore */
      }
      if (response.status === 422 || detail.toLowerCase().includes('distance')) {
        throw new Error('This route is too long. Try a shorter distance.');
      }
      throw new Error(detail);
    }

    const envelope = await response.json();
    return transformDirectionsEnvelope(envelope);
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    console.error('Directions API error:', error);
    throw error;
  }
}

/** Parse Coordal transit-route envelope → UI route + transit profile. */
export function parseTransitRouteEnvelope(envelope: {
  data?: Record<string, unknown>;
}): TransitRouteResponse {
  const data = envelope?.data ?? {};
  const route = (data.route ?? {}) as Record<string, unknown>;
  const leg = (route.legs as Array<Record<string, unknown>> | undefined)?.[0] ?? {};
  const steps = ((leg.steps as Array<Record<string, unknown>> | undefined) ?? []).map(
    (s: Record<string, unknown>) => {
      const maneuver = (s.maneuver as Record<string, unknown>) ?? {};
      return {
        instruction: (maneuver.instruction as string) || (s.name as string) || '',
        distance: s.distance as number,
        duration: s.duration as number,
        mode: (s.mode as string) || undefined,
        name: (s.name as string) || undefined,
        stop_type: (s.stop_type as string) || undefined,
        maneuver: {
          type: (maneuver.type as string) || 'continue',
          modifier: maneuver.modifier as string | undefined,
          bearing_before: undefined,
          bearing_after: undefined,
          location: maneuver.location as [number, number],
          instruction: (maneuver.instruction as string) || undefined,
        },
      };
    }
  );

  return {
    route: {
      distance: route.distance as number,
      duration: route.duration as number,
      geometry: route.geometry as GeoJSON.LineString,
      summary: (leg.summary as string) || '',
      steps,
    },
    transit_profile: data.transit_profile as TransitProfile,
  };
}

/** Get a transit route (driving route + stops along corridor) via Coordal API. */
export async function getTransitRoute(
  origin: Coordinates,
  destination: Coordinates,
  signal?: AbortSignal
): Promise<TransitRouteResponse> {
  const token = requireAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const response = await fetch(`${API_URL}/v1/navigation/transit-route`, {
    method: 'POST',
    headers,
    signal,
    body: JSON.stringify({
      origin: { latitude: origin.lat, longitude: origin.lng },
      destination: { latitude: destination.lat, longitude: destination.lng },
    }),
  });

  if (!response.ok) {
    let detail = 'Failed to get transit route';
    try {
      detail = (await response.json()).detail || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }

  const envelope = await response.json();
  return parseTransitRouteEnvelope(envelope);
}

// ── Elevation along route ────────────────────────────────────────────────────

export interface RouteElevationStats {
  totalAscent: number;
  totalDescent: number;
  minElevation: number;
  maxElevation: number;
  elevationAvailable: boolean;
}

const ELEVATION_SAMPLE_MAX = 80;

/** Uniformly sample a route LineString for elevation queries (keeps API payload small). */
export function sampleLineStringCoordinates(
  geometry: GeoJSON.LineString,
  maxPoints = ELEVATION_SAMPLE_MAX,
): Coordinates[] {
  const coords = geometry.coordinates;
  if (!coords.length) return [];
  if (coords.length <= maxPoints) {
    return coords.map(([lng, lat]) => ({ lat, lng }));
  }

  const result: Coordinates[] = [];
  const stride = (coords.length - 1) / (maxPoints - 1);
  for (let i = 0; i < maxPoints; i++) {
    const idx = Math.min(Math.round(i * stride), coords.length - 1);
    const [lng, lat] = coords[idx];
    result.push({ lat, lng });
  }
  return result;
}

/** Fetch ascent/descent stats for a route geometry. Requires signed-in user (API key). */
export async function getRouteElevation(
  geometry: GeoJSON.LineString,
  signal?: AbortSignal,
): Promise<RouteElevationStats | null> {
  const token = localStorage.getItem('auth_token');
  if (!token) return null;

  const coordinates = sampleLineStringCoordinates(geometry);
  if (coordinates.length < 2) return null;

  const response = await fetch(`${API_URL}/v1/navigation/elevation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    signal,
    body: JSON.stringify({
      coordinates: coordinates.map((c) => ({
        latitude: c.lat,
        longitude: c.lng,
      })),
    }),
  });

  if (!response.ok) return null;

  const envelope = await response.json();
  const data = envelope?.data;
  if (!data?.elevation_available) return null;

  return {
    totalAscent: data.total_ascent as number,
    totalDescent: data.total_descent as number,
    minElevation: data.min_elevation as number,
    maxElevation: data.max_elevation as number,
    elevationAvailable: true,
  };
}
